import { useSyncExternalStore } from "react";
import CommandCenterApiService from "afms/api/commandCenter.api";

// Pickup requests waiting for this vendor to confirm them (lifecycle status
// Requested), for the badge on the vendor sidebar's Pickups item. QC
// 2026-10-23: Mike looked there first for the new request — "I see no
// notifications here under pickup".
//
// Built like useInboxUnreadCount: one module-level store and one poller
// however many components show it, and a failed fetch reads as 0. 60s, half
// the inbox's rhythm, because the pickup list is the heavier call.
const POLL_MS = 60000;

let waiting = 0;
let timer = null;
// Only the newest request may write the count (see useInboxUnreadCount).
let latestRequest = 0;
const listeners = new Set();

const emit = () => listeners.forEach((listener) => listener());

/**
 * Re-reads the count now. Call after a pickup is confirmed so the badge drops
 * without waiting for the next poll. A no-op while nothing displays it.
 */
export const refreshAwaitingPickupCount = () => {
  if (listeners.size === 0) return Promise.resolve();
  latestRequest += 1;
  const request = latestRequest;
  return CommandCenterApiService.getPickupRequests({
    status: "Requested",
    pageNumber: 1,
    pageSize: 1,
  })
    .then((res) => res?.data?.data?.totalCount ?? 0)
    .catch(() => 0)
    .then((next) => {
      if (request !== latestRequest || next === waiting) return;
      waiting = next;
      emit();
    });
};

const subscribe = (listener) => {
  listeners.add(listener);
  if (listeners.size === 1) {
    refreshAwaitingPickupCount();
    timer = setInterval(refreshAwaitingPickupCount, POLL_MS);
  }
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0) {
      clearInterval(timer);
      timer = null;
      // Signed out or left the portal: the next sign-in on this tab starts
      // from nothing, not from another org's count.
      latestRequest += 1;
      waiting = 0;
    }
  };
};

const subscribeNothing = () => () => {};
const getWaiting = () => waiting;
const getNothing = () => 0;

/**
 * @param {boolean} [enabled=true] false reads 0 and does not poll.
 * @returns {number} pickups assigned to the caller's vendor org still at Requested
 */
export default function useAwaitingPickupCount(enabled = true) {
  return useSyncExternalStore(
    enabled ? subscribe : subscribeNothing,
    enabled ? getWaiting : getNothing
  );
}
